import './Quizzes.scss';

import * as React from 'react';
import * as ReactDOM from 'react-dom';

import { QuizImportMethods } from '@showbie-socrative/socrative-utils';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { CreateQuizEventAdapter } from '~analytics/adapters/CreateQuizEventAdapter';
import {
  TeacherApplicationContext,
  TeacherApplicationContextProvider,
} from '~shared/context/TeacherApplicationContext';
import { DrawerProvider } from '~teacher/components/DrawerProvider';
import { QuizPanel, WorkspaceSidebar } from '~teacher/components/quizzes';
import {
  QuizToastActions,
  apiToaster,
} from '~teacher/components/quizzes/toastActions';
import { RouterProvider } from '~teacher/context/RouterContext';
import { addQuizToCache, queryQuizLimit, quizzesQueryKey } from '~teacher/hook';
import { handleImportQuizError } from '~teacher/hook/useImportQuizToPersonalBySocMutation';
import { queryClient } from '~teacher/queryClient';

import { uniqueIdFactory } from '../../api/quiz/getQuizzes/normalize/uniqueIds';
import { importQuizToPersonalBySoc } from '../../api/quiz/importQuiz';

export function Quizzes({
  quizFolderPath,
  quizLimitReachedTime,
}: {
  quizFolderPath?: string;
  quizLimitReachedTime?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
}) {
  return (
    <div className="quizzes-container mx-auto font-sans antialiased flex">
      <DrawerProvider>
        <WorkspaceSidebar />
        <QuizPanel
          quizFolderPath={quizFolderPath}
          quizLimitReachedTime={quizLimitReachedTime}
        />
      </DrawerProvider>
    </div>
  );
}

interface DoQuizzesPageParams {
  router: any;
  quizFolderPath?: string;
  quizLimitReachedTime?: number;
}

/**
 * Current routing method requires this react-dom render method to
 * mount the page content onto the DOM.
 */
export function doQuizzesPage({
  router,
  quizFolderPath,
  quizLimitReachedTime,
}: DoQuizzesPageParams) {
  const mainContent = document.getElementById('main-content');
  // NOTE SOC-2401: This is causing issues when rendering. Please review before adding back in
  // ReactDOM.unmountComponentAtNode(mainContent);

  ReactDOM.render(
    <TeacherApplicationContextProvider
      applicationContext={TeacherApplicationContext.Socrative}
    >
      <QueryClientProvider client={queryClient}>
        <ReactQueryDevtools initialIsOpen={false} />
        <RouterProvider router={router}>
          <Quizzes
            quizFolderPath={quizFolderPath}
            quizLimitReachedTime={quizLimitReachedTime}
          />
        </RouterProvider>
      </QueryClientProvider>
    </TeacherApplicationContextProvider>,
    mainContent
  );
}

interface DoImportBySocParams {
  socNumber: string;
  isFromQuizShop: boolean;
  router: any;
}

/*
 * For use when importing quizzes using a URL. Redirects to the regular quizzes page
 */
export async function doImportBySoc({
  socNumber,
  isFromQuizShop,
  router,
}: DoImportBySocParams) {
  const { currentQuizCount, limit } = await queryQuizLimit(queryClient);
  /*  Use a number instead of a boolean for this, so that every time we reach
      the quiz limit the quiz limit pay wall can be triggered
   */
  let quizLimitReachedTime = null;
  if (isFinite(limit) && currentQuizCount + 1 > limit) {
    quizLimitReachedTime = new Date().getTime();
  }

  if (quizLimitReachedTime == null) {
    try {
      const { name, parentId, id, soc, date, sharable } =
        await importQuizToPersonalBySoc({
          parentId: null,
          socNumber: socNumber,
          importMethod: isFromQuizShop
            ? QuizImportMethods.ByQuizShop
            : QuizImportMethods.ByLink,
        });

      const quizzesCache = queryClient.getQueryData(quizzesQueryKey.quiz);

      if (quizzesCache) {
        addQuizToCache(queryClient, {
          name,
          soc,
          id,
          parentId,
          sharable,

          standard: null,
          date: new Date(date),
          type: 'quiz',
          deleted: false,
          version: 2,
          uniqueId: uniqueIdFactory.quiz.toUniqueId(id),
        });
      }

      new CreateQuizEventAdapter({
        creation_method: isFromQuizShop ? 'quiz_shop' : 'link',
        source_soc_number: socNumber,
      }).sendAnalytics();
      apiToaster([], QuizToastActions.IMPORT);
    } catch (error: unknown) {
      handleImportQuizError(() => {
        // this error means the quiz error has been exceeded
        quizLimitReachedTime = new Date().getTime();
      })(error);
    }
  }

  router.navigate('quizzes', { replace: true });

  doQuizzesPage({
    router: router,
    quizLimitReachedTime: quizLimitReachedTime,
  });
}
